import React, {useState} from 'react';
import {useQuery} from '../hooks';
import {confirmPasswordReset} from 'auth/api';
import {useForm, RegisterOptions} from 'react-hook-form';

import {
  Label,
  Title,
  PrimaryButton,
  ErrorMessage,
  CheckButton,
} from 'components';
import {makeBorderClass} from 'components/Form/Input';

import {
  I18N_PASSWORDS_DONT_MATCH,
  I18N_PASSWORD_CONFIRM,
  I18N_PASSWORD_REQUIRED,
  I18N_PASS_CONFIRM_PASSWORD_LABEL,
  I18N_PASS_CONFIRM_TITLE,
  I18N_TERMS_OF_USE_LINK_LABEL,
  I18N_TERMS_OF_USE_WARNING,
  I18N_TERMS_NOT_AGREED,
  I18N_INVALID_PASSWORD_RESET_LINK,
  I18N_MINIMUM_PASSWORD_LENGTH,
} from '../i18n';
import {useDispatch} from 'react-redux';
import {push} from 'connected-react-router';
import {ROUTE} from 'containers/App/constants';

type Inputs = {
  pass1: string;
  pass2: string;
};

type Props = {
  onShowEula: () => void;
};

export default function PasswordResetConfirmForm({onShowEula}: Props) {
  // Local state
  const [eulaAgreed, setEulaAgreed] = useState(false);
  const [eulaError, setEulaError] = useState<string>();

  // Redux
  const dispatch = useDispatch();

  // Query parameter retreival
  const query = useQuery();
  const id = query.get('id');
  const token = query.get('token');
  const paramsProvided = !!id && !!token;

  // Local state
  const [resetFailed, setResetFailed] = useState(false);

  // Get form validation stuff
  const {register, handleSubmit, errors, watch} = useForm<Inputs>();

  // Submission handler
  const onSubmit = ({pass2}: Inputs) => {
    // Check if agreed to eula
    if (!eulaAgreed) {
      setEulaError(I18N_TERMS_NOT_AGREED());
    } else if (!!id && !!token) {
      // Since we're showing an error when id or token
      // are empty following will always execute.
      confirmPasswordReset(id, pass2, token)
        .then(() => {
          dispatch(push(ROUTE.Index));
        })
        .catch(e => {
          console.error(e);
          setResetFailed(true);
        });
    }
  };

  // Validation rules
  const inputRequirements: Record<keyof Inputs, RegisterOptions> = {
    pass1: {
      required: true,
      minLength: 10,
    },
    pass2: {
      required: true,
      minLength: 10,
      // Following validation will make sure passwords match.
      validate: value => value === watch('pass1'),
    },
  };

  const handleEulaTickboxClick = () => {
    setEulaAgreed(!eulaAgreed);
    setEulaError(undefined);
  };

  return (
    <form
      className="bg-white rounded-gigantic py-6 px-8 flex flex-col z-index-50 relative"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Title */}
      <Title className="text-3xl">{I18N_PASS_CONFIRM_TITLE()}</Title>

      {/* Render form input if token and id are provided, otherwise show error */}
      {paramsProvided ? (
        <>
          {/* Passowrd 1 input field */}
          <Label
            htmlFor="pass1"
            label={I18N_PASS_CONFIRM_PASSWORD_LABEL()}
            className="mt-8"
          >
            <input
              className={`${makeBorderClass()} p-2 pl-4 w-full`}
              id="pass1"
              name="pass1"
              type="password"
              defaultValue=""
              ref={register(inputRequirements.pass1)}
            />
          </Label>

          {/* Password 1 required error message */}
          {errors.pass1 && errors.pass1.type === 'required' && (
            <ErrorMessage className="mt-4">
              {I18N_PASSWORD_REQUIRED()}
            </ErrorMessage>
          )}
          {errors.pass1 && errors.pass1.type === 'minLength' && (
            <ErrorMessage className="mt-4">
              {I18N_MINIMUM_PASSWORD_LENGTH()}
            </ErrorMessage>
          )}

          {/* Password 2 input field */}
          <Label
            htmlFor="pass2"
            label={I18N_PASS_CONFIRM_PASSWORD_LABEL()}
            className="mt-8"
          >
            <input
              className={`${makeBorderClass()} p-2 pl-4 w-full`}
              id="pass2"
              name="pass2"
              type="password"
              defaultValue=""
              ref={register(inputRequirements.pass2)}
            />
          </Label>

          {/* Password 2 required error message */}
          {errors.pass2 && errors.pass2.type === 'required' && (
            <ErrorMessage className="mt-4">
              {I18N_PASSWORD_REQUIRED()}
            </ErrorMessage>
          )}
          {errors.pass2 && errors.pass2.type === 'minLength' && (
            <ErrorMessage className="mt-4">
              {I18N_MINIMUM_PASSWORD_LENGTH()}
            </ErrorMessage>
          )}
          {errors.pass2 && errors.pass2.type === 'validate' && (
            <ErrorMessage className="mt-4">
              {I18N_PASSWORDS_DONT_MATCH()}
            </ErrorMessage>
          )}

          {/* I agree with EULA checkbox */}
          <div className="mt-4 flex">
            <CheckButton
              id="eula"
              checked={eulaAgreed}
              onClick={handleEulaTickboxClick}
            />
            <div className="ml-4">
              {I18N_TERMS_OF_USE_WARNING()}{' '}
              <span
                className="text-dodger-blue hover:underline cursor-pointer"
                onClick={onShowEula}
              >
                {I18N_TERMS_OF_USE_LINK_LABEL()}
              </span>
            </div>
          </div>
          {eulaError && <div className="mt-2 text-amaranth">{eulaError}</div>}

          {/* Submission button */}
          <PrimaryButton type="submit" className="w-full h-12 mt-4">
            {I18N_PASSWORD_CONFIRM()}
          </PrimaryButton>

          {resetFailed && (
            <ErrorMessage className="mt-4">
              {I18N_INVALID_PASSWORD_RESET_LINK()}
            </ErrorMessage>
          )}
        </>
      ) : (
        <ErrorMessage className="my-4">
          {I18N_INVALID_PASSWORD_RESET_LINK()}
        </ErrorMessage>
      )}
    </form>
  );
}
